import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
    Mail, 
    Send,
    Loader2,
    Plane,
    MessageCircle,
    Heart
} from 'lucide-react';
import { toast } from 'sonner';

// Social Media Icons as SVG components
const FacebookIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
    </svg>
);

const InstagramIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
    </svg>
);

const YouTubeIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
    </svg>
);

const TikTokIcon = () => (
    <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
    </svg>
);

export default function Contact() {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        subject: '',
        message: ''
    });
    const [sending, setSending] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.name || !formData.email || !formData.message) {
            toast.error('Please fill in all required fields');
            return;
        }

        setSending(true);
        
        // For now, open email client with pre-filled info
        const subject = encodeURIComponent(formData.subject || 'Inquiry from Flight Attendant Academy');
        const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\n${formData.message}`);
        window.location.href = `mailto:Flywithshelly@gmail.com?subject=${subject}&body=${body}`;
        
        toast.success('Opening your email client...');
        setSending(false);
    };

    const socialLinks = [
        {
            name: 'Facebook',
            url: 'https://www.facebook.com/flywithshelly',
            icon: FacebookIcon,
            color: 'hover:text-blue-600',
            bgColor: 'hover:bg-blue-50'
        },
        {
            name: 'Instagram',
            url: 'https://www.instagram.com/fly_with_shelly',
            icon: InstagramIcon,
            color: 'hover:text-pink-600',
            bgColor: 'hover:bg-pink-50'
        },
        {
            name: 'YouTube',
            url: 'https://youtube.com/@flywithshelly',
            icon: YouTubeIcon,
            color: 'hover:text-red-600',
            bgColor: 'hover:bg-red-50'
        },
        {
            name: 'TikTok',
            url: 'https://www.tiktok.com/@flyingwithshelly',
            icon: TikTokIcon,
            color: 'hover:text-black',
            bgColor: 'hover:bg-gray-50'
        }
    ];

    return (
        <div className="min-h-screen">
            {/* Hero Section */}
            <section className="py-16 md:py-24 bg-gradient-to-b from-primary/5 to-white">
                <div className="container mx-auto px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <Badge variant="secondary" className="mb-4">
                            <MessageCircle className="h-3 w-3 mr-1" />
                            Get In Touch
                        </Badge>
                        
                        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight mb-4">
                            Contact <span className="text-primary">Shelly</span>
                        </h1>
                        
                        <p className="text-lg text-muted-foreground mb-8">
                            Have questions about becoming a flight attendant? Want to learn more about coaching? 
                            I'd love to hear from you!
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Contact Section */}
            <section className="py-12">
                <div className="container mx-auto px-4">
                    <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-8">
                        {/* Contact Info */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 }}
                        >
                            <Card className="p-8 h-full">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                                        <Plane className="h-6 w-6 text-primary" />
                                    </div>
                                    <div>
                                        <h2 className="font-heading text-xl font-semibold">Flight Attendant Academy</h2>
                                        <p className="text-sm text-muted-foreground">by Shelly</p>
                                    </div>
                                </div>

                                {/* Email */}
                                <div className="mb-8">
                                    <h3 className="font-semibold mb-3">Email Me Directly</h3>
                                    <a 
                                        href="mailto:Flywithshelly@gmail.com"
                                        className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 hover:bg-primary/10 transition-colors group"
                                        data-testid="contact-email"
                                    >
                                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                                            <Mail className="h-5 w-5 text-primary" />
                                        </div>
                                        <div>
                                            <p className="font-medium">Flywithshelly@gmail.com</p>
                                            <p className="text-sm text-muted-foreground">Click to send an email</p>
                                        </div>
                                    </a>
                                </div>

                                {/* Social Links */}
                                <div>
                                    <h3 className="font-semibold mb-4">Follow Me</h3>
                                    <div className="grid grid-cols-4 gap-3">
                                        {socialLinks.map((social) => (
                                            <a
                                                key={social.name}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`flex flex-col items-center justify-center p-4 rounded-xl border transition-all ${social.bgColor} ${social.color} group`}
                                                data-testid={`social-${social.name.toLowerCase()}`}
                                                title={social.name}
                                            >
                                                <div className="text-muted-foreground group-hover:scale-110 transition-transform">
                                                    <social.icon />
                                                </div>
                                                <span className="text-xs mt-2 text-muted-foreground group-hover:text-current">
                                                    {social.name}
                                                </span>
                                            </a>
                                        ))}
                                    </div>
                                </div>

                                {/* Message */}
                                <div className="mt-8 p-4 rounded-xl bg-muted/50 text-center">
                                    <Heart className="h-6 w-6 text-primary mx-auto mb-2" />
                                    <p className="text-sm text-muted-foreground">
                                        I typically respond within 24-48 hours. For urgent coaching inquiries, 
                                        please mention "URGENT" in your subject line.
                                    </p>
                                </div>
                            </Card>
                        </motion.div>

                        {/* Contact Form */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.2 }}
                        >
                            <Card className="p-8">
                                <h2 className="font-heading text-xl font-semibold mb-6">Send a Message</h2>
                                
                                <form onSubmit={handleSubmit} className="space-y-4">
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        <div>
                                            <Label htmlFor="name">Name *</Label>
                                            <Input
                                                id="name"
                                                value={formData.name}
                                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                                placeholder="Your name"
                                                className="mt-1"
                                                data-testid="contact-name"
                                            />
                                        </div>
                                        <div>
                                            <Label htmlFor="email">Email *</Label>
                                            <Input
                                                id="email"
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                                placeholder="your@email.com"
                                                className="mt-1"
                                                data-testid="contact-email-input"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <Label htmlFor="subject">Subject</Label>
                                        <Input
                                            id="subject"
                                            value={formData.subject}
                                            onChange={(e) => setFormData({...formData, subject: e.target.value})}
                                            placeholder="What's this about?"
                                            className="mt-1"
                                            data-testid="contact-subject"
                                        />
                                    </div>

                                    <div>
                                        <Label htmlFor="message">Message *</Label>
                                        <Textarea
                                            id="message"
                                            value={formData.message}
                                            onChange={(e) => setFormData({...formData, message: e.target.value})}
                                            placeholder="Tell me about your flight attendant journey or questions..."
                                            className="mt-1 min-h-[150px]"
                                            data-testid="contact-message"
                                        />
                                    </div>

                                    <Button 
                                        type="submit" 
                                        className="w-full rounded-full"
                                        size="lg"
                                        disabled={sending}
                                        data-testid="contact-submit"
                                    >
                                        {sending ? (
                                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                        ) : (
                                            <Send className="h-4 w-4 mr-2" />
                                        )}
                                        {sending ? 'Opening Email...' : 'Send Message'}
                                    </Button>

                                    <p className="text-xs text-center text-muted-foreground">
                                        This will open your default email client with your message pre-filled.
                                    </p>
                                </form>
                            </Card>
                        </motion.div>
                    </div>
                </div>
            </section>
        </div>
    );
}
